import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import COUNTRY_CODE_OPTIONS from '../access-request/CountryCodeOptions';
import COUNTRIES from '../../data/countries';

const EXPERIENCE_OPTIONS = [
  { value: '<1', label: 'Less than 1 year' }, { value: '1-3', label: '1–3 years' },
  { value: '3-5', label: '3–5 years' }, { value: '5-10', label: '5–10 years' },
  { value: '10+', label: '10+ years' },
];
const COUNT_OPTIONS = [
  { value: 'less_than_10', label: 'Less than 10' }, { value: '11_25', label: '11–25' },
  { value: 'more_than_25', label: 'More than 25' },
];
const PORTFOLIO_OPTIONS = [
  { value: '<50K', label: 'Under $50K' }, { value: '50K-250K', label: '$50K – $250K' },
  { value: '250K-1M', label: '$250K – $1M' }, { value: '1M-5M', label: '$1M – $5M' },
  { value: '5M+', label: 'Over $5M' },
];
const INCOME_OPTIONS = [
  { value: '<50K', label: 'Under $50K' }, { value: '50K-100K', label: '$50K – $100K' },
  { value: '100K-200K', label: '$100K – $200K' }, { value: '200K-500K', label: '$200K – $500K' },
  { value: '500K-1M', label: '$500K – $1M' }, { value: '1M+', label: 'Over $1M' },
  { value: 'prefer_not_say', label: 'Prefer not to say' },
];
const NET_WORTH_OPTIONS = [
  { value: '<100K', label: 'Under $100K' }, { value: '100K-500K', label: '$100K – $500K' },
  { value: '500K-1M', label: '$500K – $1M' }, { value: '1M-5M', label: '$1M – $5M' },
  { value: '5M-10M', label: '$5M – $10M' }, { value: '10M+', label: 'Over $10M' },
  { value: 'prefer_not_say', label: 'Prefer not to say' },
];
const TYPE_OPTIONS = [
  { value: 'stocks', label: 'Stocks/ETFs' }, { value: 'bonds', label: 'Bonds/Fixed Income' },
  { value: 'real_estate', label: 'Real Estate' }, { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'private_equity', label: 'Private Equity/Venture Capital' },
  { value: 'commodities', label: 'Commodities/Precious Metals' }, { value: 'other', label: 'Other' },
];

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-gray-300 text-sm">{label}</Label>
    {children}
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

const IntroduceeProfileSection = ({ profileData, onChange, onToggleType }) => {
  const [open, setOpen] = useState(false);
  const set = (k) => (v) => onChange(k, v);
  const setText = (k) => (e) => onChange(k, e.target.value);

  return (
    <Card className="bg-gray-800 border-gray-700">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="introducee-profile-toggle"
        className="w-full text-left px-6 py-4 flex items-center justify-between text-white hover:bg-gray-750 transition"
      >
        <div>
          <p className="font-semibold">Tell us a bit about yourself <span className="text-gray-500 font-normal text-sm">(optional)</span></p>
          <p className="text-gray-400 text-xs mt-0.5">Helps us tailor your experience. You can skip this entirely and still create your account.</p>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && (
        <CardContent className="space-y-5 pt-2" data-testid="introducee-profile-content">
          <SelectField label="Investment Experience" value={profileData.investment_experience}
            onChange={set('investment_experience')} options={EXPERIENCE_OPTIONS} placeholder="Select experience level" />
          <SelectField label="Number of Investments" value={profileData.investment_count}
            onChange={set('investment_count')} options={COUNT_OPTIONS} placeholder="Select range" />
          <Field label="Phone Number">
            <div className="flex gap-2">
              <Select value={profileData.phone_country_code} onValueChange={set('phone_country_code')}>
                <SelectTrigger className="w-[140px] bg-gray-700 border-gray-600 text-white"><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">{COUNTRY_CODE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="bg-gray-700 border-gray-600 text-white flex-1" type="tel"
                placeholder="555-123-4567" value={profileData.phone_number} onChange={setText('phone_number')} />
            </div>
          </Field>
          <Field label="Occupation / Role">
            <Input className="bg-gray-700 border-gray-600 text-white"
              placeholder="e.g. Investment Banker, Software Engineer"
              value={profileData.occupation} onChange={setText('occupation')} />
          </Field>
          <Field label="LinkedIn Profile URL">
            <Input className="bg-gray-700 border-gray-600 text-white" type="url"
              placeholder="https://linkedin.com/in/yourprofile"
              value={profileData.linkedin_profile} onChange={setText('linkedin_profile')} />
          </Field>
          <SelectField label="Total Portfolio Value" value={profileData.portfolio_value}
            onChange={set('portfolio_value')} options={PORTFOLIO_OPTIONS} placeholder="Select range" />
          <Field label="Investment Types">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TYPE_OPTIONS.map(t => (
                <label key={t.value} className="flex items-center space-x-2 text-gray-300 text-sm cursor-pointer">
                  <input type="checkbox" checked={profileData.investment_types.includes(t.value)}
                    onChange={() => onToggleType(t.value)}
                    className="rounded border-gray-600 bg-gray-700" />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>
          </Field>
          <SelectField label="Annual Income" value={profileData.annual_income}
            onChange={set('annual_income')} options={INCOME_OPTIONS} placeholder="Select range" />
          <SelectField label="Net Worth" value={profileData.net_worth}
            onChange={set('net_worth')} options={NET_WORTH_OPTIONS} placeholder="Select range" />
        </CardContent>
      )}
    </Card>
  );
};

export const INITIAL_INTRODUCEE_PROFILE = {
  country: '', investment_experience: '', investment_count: '',
  phone_country_code: '', phone_number: '', occupation: '', linkedin_profile: '',
  portfolio_value: '', investment_types: [], annual_income: '', net_worth: '',
};

export const IntroduceeCountryCard = ({ profileData, onChange }) => (
  <Card className="bg-gray-800 border-emerald-500/40 border" data-testid="introducee-country-card">
    <CardContent className="pt-6 space-y-2">
      <Label className="text-white text-sm font-semibold">
        Country <span className="text-red-400">*</span>
      </Label>
      <p className="text-gray-400 text-xs">Required — we use this for your currency, time zone and reporting defaults.</p>
      <Select value={profileData.country} onValueChange={(v) => onChange('country', v)}>
        <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="introducee-country-select">
          <SelectValue placeholder="Select your country" />
        </SelectTrigger>
        <SelectContent>
          {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </CardContent>
  </Card>
);

export default IntroduceeProfileSection;
